import { css, Rule } from 'glamor'
import * as PropTypes from 'prop-types'
import * as React from 'react'

import { useTheme } from '../../theme'

import { Touchable } from '../touchable'
import { Flex } from '../flex'

import WarningIcon from './warning.svg'
import getStyles from './styles'

function wrap(content: React.ReactNode, style: Rule, onClick?: () => void) {
  return onClick ? (
    <Touchable onClick={onClick} {...css(style)} alignItems="center" justifyContent="center">
      {content}
    </Touchable>
  ) : (
    <Flex {...css(style)} alignItems="center" justifyContent="center">
      {content}
    </Flex>
  )
}

interface IThumbnailProps {
  error?: Error | null
  loading?: boolean
  size?: 'small' | 'medium' | 'large'
  src?: string
  onClick?: () => void
  children?: React.ReactNode
}

export function Thumbnail({ error, loading, size, src, onClick, children }: IThumbnailProps) {
  const styles = getStyles(useTheme())

  const style = {
    ...styles.thumbnail.base,
    ...(size ? styles.thumbnail.sizes[size] : null),
    ...(typeof onClick === 'function' ? styles.thumbnail.clickable : {}),
  }

  return wrap(
    loading ? null : error ? (
      <WarningIcon />
    ) : src ? (
      <>
        <img alt="thumbnail" src={src} {...css(styles.img)} />
        {children}
      </>
    ) : null,
    [style, (loading || error) && styles.state],
    onClick
  )
}

Thumbnail.propTypes = {
  error: PropTypes.shape({
    title: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    detail: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  src: PropTypes.string,
  children: PropTypes.node,
}

Thumbnail.defaultProps = {
  error: null,
  loading: false,
  size: 'medium',
}
